import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

/**
 * Well-known feed/kill pairs in the Gray-Scott parameter space.
 *
 * Most of that space produces either a blank field or uniform mush; these are the
 * pockets that produce structure, and offering them as presets is far more useful than
 * asking someone to find them with two sliders.
 */
export const GRAY_SCOTT_PRESETS = [
  { value: 'corals', label: 'Corals', feed: 0.0545, kill: 0.062 },
  { value: 'mitosis', label: 'Mitosis', feed: 0.0367, kill: 0.0649 },
  { value: 'fingerprints', label: 'Fingerprints', feed: 0.037, kill: 0.06 },
  { value: 'solitons', label: 'Solitons', feed: 0.03, kill: 0.062 },
  { value: 'worms', label: 'Worms', feed: 0.078, kill: 0.061 },
  { value: 'spirals', label: 'Spirals', feed: 0.018, kill: 0.051 },
  { value: 'custom', label: 'Eigene Werte', feed: 0.055, kill: 0.062 },
] as const;

export const REACTION_DIFFUSION_PARAMS = [
  {
    kind: 'enum',
    key: 'preset',
    label: 'Muster',
    default: 'corals',
    group: 'Chemie',
    options: GRAY_SCOTT_PRESETS.map((p) => ({ value: p.value, label: p.label })),
    tooltip: 'Bekannte Punkte im Gray-Scott-Parameterraum.',
  },
  {
    kind: 'float',
    key: 'feed',
    label: 'Feed-Rate',
    min: 0.005,
    max: 0.11,
    step: 0.0005,
    default: 0.0545,
    precision: 4,
    group: 'Chemie',
    showIf: (v) => v.preset === 'custom',
  },
  {
    kind: 'float',
    key: 'kill',
    label: 'Kill-Rate',
    min: 0.03,
    max: 0.075,
    step: 0.0005,
    default: 0.062,
    precision: 4,
    group: 'Chemie',
    showIf: (v) => v.preset === 'custom',
  },
  {
    kind: 'float',
    key: 'diffusionA',
    label: 'Diffusion A',
    min: 0.2,
    max: 1.4,
    step: 0.01,
    default: 1,
    group: 'Chemie',
  },
  {
    kind: 'float',
    key: 'diffusionB',
    label: 'Diffusion B',
    min: 0.1,
    max: 1,
    step: 0.01,
    default: 0.5,
    group: 'Chemie',
  },
  {
    kind: 'float',
    key: 'anisotropy',
    label: 'Anisotropie',
    min: -0.8,
    max: 0.8,
    step: 0.01,
    default: 0,
    group: 'Chemie',
    tooltip: 'Streckt die Diffusion in eine Richtung — das Muster wird gerichtet.',
  },

  {
    kind: 'enum',
    key: 'seedMode',
    label: 'Startmuster',
    default: 'text',
    group: 'Start',
    options: [
      { value: 'text', label: 'Text als B-Konzentration' },
      { value: 'outline', label: 'Textkontur' },
      { value: 'random', label: 'Zufallsflecken' },
      { value: 'textAndNoise', label: 'Text und Flecken' },
    ],
  },
  {
    kind: 'bool',
    key: 'textBoundary',
    label: 'Text als Randbedingung',
    default: false,
    group: 'Start',
    tooltip: 'Hält die Reaktion innerhalb (oder ausserhalb) der Buchstaben fest.',
  },
  {
    kind: 'int',
    key: 'resolution',
    label: 'Simulationsauflösung',
    min: 128,
    max: 2048,
    step: 32,
    default: 700,
    unit: 'px',
    neverRandomise: true,
    group: 'Start',
  },
  {
    kind: 'int',
    key: 'totalSteps',
    label: 'Gesamtschritte',
    min: 100,
    max: 20000,
    step: 100,
    default: 4000,
    bulkRange: [800, 9000],
    group: 'Start',
  },
  {
    kind: 'int',
    key: 'stepsPerFrame',
    label: 'Iterationen pro Frame',
    min: 1,
    max: 120,
    step: 1,
    default: 40,
    neverRandomise: true,
    group: 'Start',
  },

  {
    kind: 'palette',
    key: 'palette',
    label: 'Farbrampe',
    default: { ...DEFAULT_PALETTE, paletteId: 'ink' },
    group: 'Farbe',
  },
  {
    kind: 'float',
    key: 'contrast',
    label: 'Kontrast',
    min: 0.2,
    max: 6,
    step: 0.05,
    default: 1.6,
    group: 'Farbe',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type ReactionDiffusionValues = SchemaValues<typeof REACTION_DIFFUSION_PARAMS>;
